import React from 'react';
import {
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import PlayerCard from './PlayerCard';
import './TeeTimeGroup.css';

function TeeTimeGroup({ group, conflicts, getPlayerMeritRank, getPlayerPaceRank, isActive, onMoveGroupUp, onMoveGroupDown, onDeleteGroup, isFirst, isLast, totalGroups, feedbackByPlayer, onFeedbackClick, requestFulfillment, swapMode, swapSelection, onSwapSelect }) {
  const hasConflicts = conflicts.length > 0;

  // Check if this group is selected in swap mode
  const isGroupSelected = swapMode === 'group' && swapSelection?.groupId === group.id;

  const groupClasses = [
    'tee-time-group',
    hasConflicts ? 'has-conflicts' : '',
    isActive ? 'drag-active' : '',
    swapMode ? 'swap-mode-active' : '',
    isGroupSelected ? 'swap-selected' : ''
  ].filter(Boolean).join(' ');

  const getPaceStatus = () => {
    if (group.paceStatus === 'normal') return 'Pace Valid';
    if (group.paceStatus === 'warning') return 'Pace Warning';
    if (group.paceStatus === 'optimized') return 'Pace Optimized';
    if (group.paceStatus === 'valid') return 'Pace Valid';
    return 'Pace Warning';
  };

  const getPaceStatusClass = () => {
    if (group.paceStatus === 'normal') return 'pace-valid';
    if (group.paceStatus === 'warning') return 'pace-warning';
    if (group.paceStatus === 'optimized') return 'pace-optimized';
    if (group.paceStatus === 'valid') return 'pace-valid';
    return 'pace-warning';
  };

  const getConflictTypes = () => {
    const types = new Set();
    conflicts.forEach(conflict => {
      types.add(conflict.type);
    });
    return Array.from(types);
  };

  const getTotalPaceRanks = () => {
    if (!group.players || group.players.length === 0) return 0;
    
    let total = 0;
    group.players.forEach(player => {
      const paceRank = getPlayerPaceRank(player.ghin);
      if (paceRank !== 'N/A') {
        total += parseInt(paceRank);
      }
    });
    return total;
  };

  return (
    <SortableContext 
      items={group.players.map(p => p.ghin)}
      strategy={verticalListSortingStrategy}
    >
      <tr className={`tee-time-row ${groupClasses}`}>
        <td className="tee-time-info">
          <div className="group-header-compact">
            <div className="group-info">
              <h4>Group {group.id}</h4>
              <div className="time-and-count">
                <span className="tee-time">{group.teeTime}</span>
                <span className={`player-count-header ${group.players.length > 4 ? 'over-capacity' : ''}`}>
                  ({group.players.length})
                </span>
              </div>
            </div>
            
            <div className="group-controls">
              <div className="group-reorder-controls">
                <button 
                  className="move-group-btn"
                  onClick={() => onMoveGroupUp(group.id)}
                  disabled={isFirst}
                  title="Move group up"
                >
                  ↑
                </button>
                <button 
                  className="move-group-btn"
                  onClick={() => onMoveGroupDown(group.id)}
                  disabled={isLast}
                  title="Move group down"
                >
                  ↓
                </button>
                {totalGroups > 1 && group.players.length === 0 && (
                  <button 
                    className="delete-group-btn"
                    onClick={() => onDeleteGroup(group.id)}
                    title="Delete empty group"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            <div className="group-status">
              <span className={`pace-status ${getPaceStatusClass()}`}>
                {getPaceStatus()}
              </span>
              <span className={`player-count ${group.players.length > 4 ? 'over-capacity' : ''}`}>
                {group.players.length}/4{group.players.length > 4 ? ' (OVER)' : ''}
              </span>
              {group.players.length > 0 && (
                <span className="pace-breakdown" title="Pace breakdown: Fast (top 25%) / Mid (middle 50%) / Slow (bottom 25%)">
                  F:{group.fastPlayerCount || 0} M:{group.midPlayerCount || 0} S:{group.slowPlayerCount || 0}
                  {group.noDataCount > 0 && ` ND:${group.noDataCount}`}
                </span>
              )}
              {group.players.length > 0 && (
                <span className="pace-total" title="Total of all pace ranks in group">
                  Total: {getTotalPaceRanks()}
                </span>
              )}
            </div>

            {hasConflicts && (
              <div className="conflict-indicator-compact">
                <span className="conflict-icon">⚠️</span>
                <span className="conflict-text">
                  {getConflictTypes().join(', ').replace(/_/g, ' ')}
                </span>
              </div>
            )}
          </div>
        </td>
        
        {Array.from({ length: Math.max(4, group.players.length) }).map((_, slotIndex) => (
          <PlayerSlot
            key={slotIndex}
            groupId={group.id}
            position={slotIndex}
            player={group.players[slotIndex]}
            getPlayerMeritRank={getPlayerMeritRank}
            getPlayerPaceRank={getPlayerPaceRank}
            hasConflict={group.players[slotIndex] ? conflicts.some(c =>
              c.player?.ghin === group.players[slotIndex].ghin ||
              c.earlyPlayers?.some(p => p.ghin === group.players[slotIndex].ghin) ||
              c.latePlayers?.some(p => p.ghin === group.players[slotIndex].ghin) ||
              c.slowPlayers?.some(p => p.ghin === group.players[slotIndex].ghin)
            ) : false}
            feedbackByPlayer={feedbackByPlayer}
            onFeedbackClick={onFeedbackClick}
            requestFulfillment={requestFulfillment}
            swapMode={swapMode}
            swapSelection={swapSelection}
            onSwapSelect={onSwapSelect}
            groupPlayers={group.players}
          />
        ))}
      </tr>
    </SortableContext>
  );
}

function PlayerSlot({ groupId, position, player, getPlayerMeritRank, getPlayerPaceRank, hasConflict, feedbackByPlayer, onFeedbackClick, requestFulfillment, swapMode, swapSelection, onSwapSelect, groupPlayers }) {
  const {
    isOver,
    setNodeRef: setDropRef,
  } = useDroppable({
    id: `position-${groupId}-${position}`,
  });

  // Determine if this slot is selected in swap mode
  const isSelected = swapSelection && swapSelection.groupId === groupId && (
    swapMode === 'group' ||
    swapSelection.position === position ||
    (swapMode === 'two' && (swapSelection.position === position || swapSelection.position + 1 === position))
  );

  // Handle swap mode click
  const handleSwapClick = (e) => {
    if (swapMode && onSwapSelect) {
      e.stopPropagation();
      e.preventDefault();
      onSwapSelect(groupId, position);
    }
  };

  const slotClasses = [
    'player-slot',
    isOver ? 'slot-drag-over' : '',
    player ? 'occupied' : 'empty',
    swapMode ? 'swap-clickable' : '',
    isSelected ? 'swap-selected' : ''
  ].filter(Boolean).join(' ');

  return (
    <td
      className={slotClasses}
      ref={setDropRef}
      onClick={swapMode ? handleSwapClick : undefined}
      style={swapMode ? { cursor: 'pointer' } : undefined}
    >
      {player ? (
        <DraggablePlayer
          player={player}
          meritRank={getPlayerMeritRank(player.ghin)}
          paceRank={getPlayerPaceRank(player.ghin)}
          hasConflict={hasConflict}
          feedbackByPlayer={feedbackByPlayer}
          onFeedbackClick={onFeedbackClick}
          requestFulfillment={requestFulfillment}
          swapMode={swapMode}
          isSelected={isSelected}
        />
      ) : (
        <div className="empty-slot">
          <span className="slot-placeholder">{swapMode ? 'Click to swap here' : 'Empty'}</span>
        </div>
      )}
    </td>
  );
}

function DraggablePlayer({ player, meritRank, paceRank, hasConflict, feedbackByPlayer, onFeedbackClick, requestFulfillment, swapMode, isSelected }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: player.ghin,
    // Disable dragging when in swap mode
    disabled: !!swapMode,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  };

  const playerFeedback = feedbackByPlayer?.[player.ghin] || null;

  // Look up per-player fulfillment data
  const partnerFulfillment = requestFulfillment?.partner?.[player.ghin] || null;
  const timeFulfillment = requestFulfillment?.time?.[player.ghin] || null;

  const wrapperClasses = [
    'draggable-player-wrapper',
    isSelected ? 'swap-selected' : '',
    swapMode ? 'swap-mode' : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={wrapperClasses}
      {...(swapMode ? {} : { ...attributes, ...listeners })}
    >
      <PlayerCard
        player={player}
        meritRank={meritRank}
        paceRank={paceRank}
        hasConflict={hasConflict}
        isDragging={isDragging}
        playerFeedback={playerFeedback}
        onFeedbackClick={onFeedbackClick}
        partnerFulfillment={partnerFulfillment}
        timeFulfillment={timeFulfillment}
      />
    </div>
  );
}

export default TeeTimeGroup;